import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {BaseLayerType} from '@components/openstreetmap/controllers/map-layers.controller';

export interface SatelliteStyleMenuItem {
  label: string;
  value?: string;
  children?: SatelliteStyleMenuItem[];
}

const INDEXES_LABEL = 'INDEX_TILE_MAP.INDEXES';

@Component({
  selector: 'app-satellite-style-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './satellite-style-selector.component.html',
  styleUrls: ['./satellite-style-selector.component.scss'],
})
export class SatelliteStyleSelectorComponent implements OnChanges {
  @Input() styles: SatelliteStyleMenuItem[] = [];
  @Input() selected: string | null = null;
  @Input() selectedIndex: string | null = null;
  @Input() visible = false;
  @Input() headerLabel = 'Select satellite source';
  @Input() allowDeselect = false;
  @Input() dropDirection: 'up' | 'down' = 'down';
  @Input() enableIndexSearch = false;
  @Output() select = new EventEmitter<BaseLayerType>();
  @Output() close = new EventEmitter<void>();

  hoveredItem: SatelliteStyleMenuItem | null = null;
  hoveredChild: SatelliteStyleMenuItem | null = null;
  pinnedIndexesPanel = false;
  indexSearchQuery = '';

  constructor(private readonly hostRef: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && !this.visible) {
      this.resetHover();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.visible && this.pinnedIndexesPanel && !this.hostRef.nativeElement.contains(event.target as Node)) {
      this.resetHover();
    }
  }

  isIndexesMenu(item: SatelliteStyleMenuItem | null | undefined): boolean {
    return item?.label === INDEXES_LABEL;
  }

  isSelected(item: SatelliteStyleMenuItem): boolean {
    const value = item.value;
    if (!value) {
      return false;
    }
    if (value.startsWith('index:')) {
      return this.selectedIndex === value.slice(6);
    }
    if (/^(sentinel|landsat):(range|all)$/.test(value)) {
      return this.selected === value;
    }
    if (value === 'plan' || value === 'google') {
      return this.selected === value;
    }
    if (value === 'sentinel' || value === 'landsat' || value === 'uav') {
      return this.selected === value && !this.selectedIndex;
    }
    return value === this.selected;
  }

  isNestedSubmenuVisible(child: SatelliteStyleMenuItem): boolean {
    if (this.isIndexesMenu(child)) {
      return this.pinnedIndexesPanel || this.hoveredChild === child;
    }
    return this.hoveredChild === child && !!child.children?.length;
  }

  get filteredIndexItems(): SatelliteStyleMenuItem[] {
    const items = this.hoveredChild?.children ?? [];
    const query = this.indexSearchQuery.trim().toLowerCase();
    return query ? items.filter((item) => item.label.toLowerCase().includes(query)) : items;
  }

  onItemMouseEnter(item: SatelliteStyleMenuItem, level: 0 | 1): void {
    if (level === 0) {
      this.hoveredItem = item;
      if (!this.pinnedIndexesPanel) {
        this.hoveredChild = null;
        this.indexSearchQuery = '';
      }
      return;
    }
    this.hoveredChild = item;
    if (!this.isIndexesMenu(item)) {
      this.pinnedIndexesPanel = false;
      this.indexSearchQuery = '';
    }
  }

  onPanelMouseLeave(event: MouseEvent): void {
    const related = event.relatedTarget as Node | null;
    if (related && this.hostRef.nativeElement.contains(related)) {
      return;
    }
    if (!this.pinnedIndexesPanel) {
      this.resetHover();
    }
  }

  onIndexesMenuClick(event: MouseEvent, child: SatelliteStyleMenuItem): void {
    event.preventDefault();
    event.stopPropagation();
    this.hoveredItem = this.styles.find((item) => item.children?.includes(child)) ?? this.hoveredItem;
    this.hoveredChild = child;
    this.pinnedIndexesPanel = !this.pinnedIndexesPanel;
    if (!this.pinnedIndexesPanel) {
      this.indexSearchQuery = '';
    }
  }

  onItemClick(item: SatelliteStyleMenuItem): void {
    if (item.children?.length || item.value === undefined) {
      return;
    }
    if (this.allowDeselect && this.isSelected(item)) {
      this.select.emit(null as unknown as BaseLayerType);
    } else {
      this.select.emit(item.value as BaseLayerType);
    }
    this.onClose();
  }

  onClose(): void {
    this.resetHover();
    this.close.emit();
  }

  private resetHover(): void {
    this.hoveredItem = null;
    this.hoveredChild = null;
    this.pinnedIndexesPanel = false;
    this.indexSearchQuery = '';
  }
}
